import {
  AlertCircle,
  ArrowRight,
  Eye,
  EyeOff,
  Mail,
  Phone,
  UserRound,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "../../api/client";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import { loadGoogleIdentity, startAppleSignIn } from "../../utils/socialAuth";

type AuthMode = "login" | "register";

const TRUST_POINTS = [
  {
    title: "Tickets saved to your account",
    body: "Open your QR codes from any device with no email lookup needed.",
  },
  {
    title: "Fast return checkout",
    body: "Your details are pre-filled the next time you check out.",
  },
  {
    title: "Secure by default",
    body: "Sessions auto-expire after inactivity to keep your account safe.",
  },
] satisfies Array<{ title: string; body: string }>;

export default function CustomerLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { customer, signIn, signInWithSocial, signUp } = useCustomerAuth();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const [mode, setMode] = useState<AuthMode>("login");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const redirectPath = searchParams.get("redirect") || "/tickets";
  const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim();
  const appleClientId = (import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined)?.trim();

  useEffect(() => {
    if (customer) {
      navigate(redirectPath, { replace: true });
    }
  }, [customer, navigate, redirectPath]);

  const submitLabel = useMemo(() => {
    if (isSubmitting) return mode === "login" ? "Signing in..." : "Creating account...";
    return mode === "login" ? "Sign in" : "Create account";
  }, [isSubmitting, mode]);

  const handleSubmit = async (submitEvent: React.FormEvent<HTMLFormElement>) => {
    submitEvent.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (mode === "login") {
        await signIn(form.email, form.password);
      } else {
        await signUp(form);
      }
      navigate(redirectPath, { replace: true });
    } catch (requestError) {
      if (mode === "login" && requestError instanceof ApiError && requestError.status === 409) {
        setMode("register");
      }
      setError(
        requestError instanceof Error ? requestError.message : "Could not complete sign in"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialLogin = useCallback(
    async (provider: "google" | "apple", idToken: string, name?: string) => {
      setError(null);
      setIsSubmitting(true);

      try {
        await signInWithSocial({ provider, idToken, name });
        navigate(redirectPath, { replace: true });
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : "Could not complete social sign in");
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, redirectPath, signInWithSocial]
  );

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) {
      return;
    }

    const target = googleButtonRef.current;
    let disposed = false;

    void loadGoogleIdentity()
      .then((googleId) => {
        if (disposed) return;
        target.innerHTML = "";
        googleId.initialize({
          client_id: googleClientId,
          ux_mode: "popup",
          callback: (response) => {
            if (response.credential) {
              void handleSocialLogin("google", response.credential);
            } else {
              setError("Google did not return a sign-in token");
            }
          },
        });
        googleId.renderButton(target, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: mode === "login" ? "signin_with" : "signup_with",
          shape: "rectangular",
          width: 360,
        });
      })
      .catch((requestError) => {
        if (!disposed) {
          setError(requestError instanceof Error ? requestError.message : "Could not load Google sign in");
        }
      });

    return () => {
      disposed = true;
      target.innerHTML = "";
    };
  }, [googleClientId, handleSocialLogin, mode]);

  const handleAppleLogin = async () => {
    setError(null);
    try {
      const response = await startAppleSignIn();
      await handleSocialLogin("apple", response.idToken, response.name);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not complete Apple sign in");
    }
  };

  return (
    <div className="aan-ticket-flow min-h-screen bg-black text-white">
      <Navbar />

      <section className="relative overflow-hidden pb-20 pt-36">
        <div
          className="absolute inset-0 bg-[url('/assets/hero-bg.jpeg')] bg-cover bg-center opacity-30"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/55" aria-hidden="true" />

        <div className="section-frame relative">
          <div className="grid gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
            <div>
              <img src="/assets/logo-white.png" alt="African Art Network" className="mb-8 w-48" />
              <div className="font-display text-xs font-semibold uppercase tracking-[0.32em] text-aan-bright">
                Secure AAN Tickets
              </div>
              <h1 className="mt-4 max-w-3xl font-display text-5xl font-black uppercase leading-none text-white sm:text-6xl">
                {mode === "login" ? "Sign in for your tickets." : "Create your AAN ticket account."}
              </h1>
              <p className="mt-5 max-w-xl text-sm leading-7 text-white/75">
                {mode === "login"
                  ? "Access your QR passes, continue checkout, and keep every African Art Network order in one place."
                  : "Set up your account once, then buy AAN events and reopen your tickets whenever you return."}
              </p>

              <div className="mt-8 hidden gap-4 sm:grid-cols-3 lg:grid lg:max-w-4xl">
                {TRUST_POINTS.map((point) => (
                  <div key={point.title} className="rounded-[8px] border border-white/15 bg-black/75 p-5">
                    <div className="mb-4 h-1 w-12 bg-aan-green" />
                    <h2 className="font-display text-lg font-black uppercase leading-none text-white">{point.title}</h2>
                    <p className="mt-3 text-xs leading-6 text-white/65">{point.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="surface-card overflow-hidden border-aan-green/70">
              <div className="border-b border-white/15 bg-aan-footer/60 p-5">
                <div className="grid grid-cols-2 gap-2 rounded-full border border-white/15 bg-black p-1">
                  {(["login", "register"] as AuthMode[]).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        setMode(option);
                        setError(null);
                      }}
                      className={`rounded-full px-4 py-2.5 font-display text-xs font-black uppercase transition ${
                        mode === option ? "bg-aan-green text-black" : "text-white/70 hover:text-white"
                      }`}
                    >
                      {option === "login" ? "Sign in" : "Create account"}
                    </button>
                  ))}
                </div>
              </div>

              <form className="space-y-4 p-6 sm:p-8" onSubmit={handleSubmit}>
                <div className="space-y-3">
                  {googleClientId ? (
                    <div className="flex justify-center overflow-hidden rounded-[8px] bg-white py-2" ref={googleButtonRef} />
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="flex w-full items-center justify-center rounded-full border border-white/15 bg-white px-4 py-3 font-display text-xs font-black uppercase text-black"
                    >
                      Google sign-in not configured
                    </button>
                  )}

                  <button
                    type="button"
                    disabled={!appleClientId || isSubmitting}
                    onClick={handleAppleLogin}
                    className="flex w-full items-center justify-center rounded-full border border-white/15 bg-black px-4 py-3 font-display text-xs font-black uppercase text-white transition hover:border-aan-green hover:text-aan-bright disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {appleClientId ? "Continue with Apple" : "Apple sign-in not configured"}
                  </button>

                  <div className="flex items-center gap-3 py-1">
                    <div className="h-px flex-1 bg-white/15" />
                    <span className="font-display text-[11px] font-black uppercase tracking-[0.18em] text-white/50">or</span>
                    <div className="h-px flex-1 bg-white/15" />
                  </div>
                </div>

                {mode === "register" && (
                  <>
                    <Field
                      label="Full name"
                      icon={UserRound}
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={(value) => setForm((current) => ({ ...current, name: value }))}
                      required
                    />
                    <Field
                      label="Phone number"
                      icon={Phone}
                      type="tel"
                      autoComplete="tel"
                      placeholder="0244 000 000"
                      value={form.phone}
                      onChange={(value) => setForm((current) => ({ ...current, phone: value }))}
                      required
                    />
                  </>
                )}

                <Field
                  label="Email address"
                  icon={Mail}
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(value) => setForm((current) => ({ ...current, email: value }))}
                  required
                />

                <div>
                  <label className="mb-1.5 block font-display text-xs font-black uppercase text-white">Password</label>
                  <div
                    className={`flex items-center gap-2 rounded-[8px] border bg-black px-3 py-3 transition focus-within:border-aan-green ${
                      error ? "border-aan-red" : "border-white/20"
                    }`}
                  >
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      minLength={mode === "register" ? 8 : undefined}
                      autoComplete={mode === "login" ? "current-password" : "new-password"}
                      placeholder={mode === "register" ? "At least 8 characters" : "Password"}
                      value={form.password}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, password: event.target.value }))
                      }
                      className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/40"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="rounded-md p-1 text-white/55 transition hover:bg-white/10 hover:text-white"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {mode === "register" && (
                    <p className="mt-1 text-[11px] text-white/50">
                      Use 8+ characters with a mix of letters and numbers.
                    </p>
                  )}
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 rounded-[8px] border border-aan-red/40 bg-aan-red/10 px-3 py-2.5 text-sm text-red-100">
                    <AlertCircle size={16} className="mt-0.5 shrink-0 text-aan-red" />
                    <span className="font-semibold">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-aan-green px-5 py-3 font-display text-sm font-black uppercase text-black transition hover:bg-aan-bright disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitLabel}
                  {!isSubmitting && <ArrowRight size={16} />}
                </button>

                <p className="text-center text-xs text-white/65">
                  {mode === "login" ? (
                    <>
                      New here?{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setMode("register");
                          setError(null);
                        }}
                        className="font-semibold text-aan-bright hover:underline"
                      >
                        Create an account
                      </button>
                    </>
                  ) : (
                    <>
                      Already have an account?{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setMode("login");
                          setError(null);
                        }}
                        className="font-semibold text-aan-bright hover:underline"
                      >
                        Sign in
                      </button>
                    </>
                  )}
                </p>
              </form>

              <div className="border-t border-white/15 bg-aan-footer/45 px-6 py-4 text-center text-[11px] text-white/50">
                By {mode === "login" ? "signing in" : "creating an account"}, you agree to the ticketing terms for
                African Art Network events.
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({
  label,
  icon: Icon,
  type,
  value,
  onChange,
  required,
  placeholder,
  autoComplete,
}: {
  label: string;
  icon: typeof UserRound;
  type: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-display text-xs font-black uppercase text-white">{label}</span>
      <div className="flex items-center gap-2 rounded-[8px] border border-white/20 bg-black px-3 py-3 transition focus-within:border-aan-green">
        <Icon size={16} className="shrink-0 text-aan-bright" />
        <input
          type={type}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/40"
        />
      </div>
    </label>
  );
}
